import { useEffect, useState } from "react";

/**
 * SSR-safe media-query hook. Returns whether `query` currently matches and
 * re-renders on change. Defaults to `false` when `window` is unavailable.
 */
export function useMediaQuery(query: string): boolean {
  const getMatch = () =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false;

  const [matches, setMatches] = useState<boolean>(getMatch);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange(); // sync in case it changed between render and effect
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

/**
 * Tailwind's `sm` breakpoint as a media query. The house layout breakpoint —
 * exported so JS gates and the `sm:` classes beside them cannot drift, and so
 * tests can stub matchMedia against the exact same string.
 */
export const SM_UP_QUERY = "(min-width: 640px)";

/**
 * A PRIMARY pointing device that hovers — i.e. a mouse or trackpad, not a
 * finger. Same query as `hasHover()` in `lib/utils/pointer.ts`, exported here
 * because that helper is a bare, non-reactive read meant for event handlers:
 * calling it during render neither re-renders on a device change nor lets a
 * test stub the viewport the way `stubPointerDevice` does. Shared as a
 * constant so the JS gate and any `(hover: hover)` CSS cannot drift.
 */
export const HOVER_QUERY = "(hover: hover) and (pointer: fine)";

/** Desktop = Tailwind `md` and up (>=768px). The spotlight tour is desktop-only. */
export function useIsDesktop(): boolean {
  return useMediaQuery("(min-width: 768px)");
}
